/**
 * Blocks on the 03 drawing that carry a NETWORK_ID or AMP_ID attribute left blank, counted per
 * block name and per attribute. A blank NETWORK_ID drops the device from every network riser and a
 * blank AMP_ID drops it from its loudspeaker zone, so a template is never built from such a drawing.
 */
export type BlankAttributeCounts = Readonly<Record<string, Readonly<Record<string, number>>>>;

export const REQUIRED_DEVICE_ATTRIBUTES = ["NETWORK_ID", "AMP_ID"] as const;

/**
 * Blanks that are sometimes right. A CZA-1300 installed in a classroom for a 70V classroom system
 * serves no paging zone, so its AMP_ID is left blank on purpose (per Denise, 2026-10-21).
 */
export const MAY_BE_BLANK: Readonly<Record<string, readonly string[]>> = { INTERCOM_CZA1300: ["AMP_ID"] };

export const BLANK_ATTRIBUTES_ADVICE =
  "Fill in these attributes on the 03 drawing, save it, and load it here again before creating the template.";

function describe(counts: BlankAttributeCounts, include: (block: string, tag: string) => boolean): string[] {
  return Object.keys(counts)
    .sort((a, b) => a.localeCompare(b))
    .map((block) => {
      const missing = REQUIRED_DEVICE_ATTRIBUTES
        .filter((tag) => (counts[block]?.[tag] ?? 0) > 0 && include(block, tag))
        .map((tag) => `${counts[block]?.[tag]} missing ${tag}`);
      return missing.length > 0 ? `${block}: ${missing.join(", ")}` : "";
    })
    .filter((line) => line !== "");
}

const mayBeBlank = (block: string, tag: string): boolean => MAY_BE_BLANK[block.toUpperCase()]?.includes(tag) ?? false;

/** The blanks that block a build, one line per block, e.g. "CLASSROOM_MS-700: 3 missing NETWORK_ID". */
export function blankAttributeLines(counts: BlankAttributeCounts = {}): string[] {
  return describe(counts, (block, tag) => !mayBeBlank(block, tag));
}

/** The blanks that only need confirming, each as a full reminder. */
export function blankAttributeReminders(counts: BlankAttributeCounts = {}): string[] {
  return describe(counts, mayBeBlank).map(
    (line) =>
      `${line}. That is only right for a 70V classroom system with the CZA-1300 installed in the room, not in a network closet - confirm that, or fill in its AMP_ID.`,
  );
}
